import type {
  Company,
  CompanyAddress,
  CompanyCaseRow,
  CompanyEntity,
  CompanyListRow,
  ContactRole,
  Deadline,
  PortalAccess
} from "./types";
import { calendarDay } from "../caseIde/header/caseHeaderModel";

/** "an H-1B", "an O-1A", "a PERM", "a TN": letters read by name take "an". */
export const withArticle = (caseType: string): string =>
  `${/^([aeiou]|[fhlmnrsx][-\d])/i.test(caseType) ? "an" : "a"} ${caseType}`;

export const initialsOf = (name: string): string => {
  const words = name
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter(
      (w) => !/^(inc|llc|ltd|corp|co|lp|llp|plc|the|of|and|for|at)$/i.test(w)
    );
  return (
    words.length > 1 ? words[0][0] + words[1][0] : words[0]?.slice(0, 2) ?? "?"
  ).toUpperCase();
};

export const formatAddress = (
  a: Partial<CompanyAddress> | null | undefined
): string => {
  if (!a) return "";
  const cityState = [a.city, [a.state, a.zip_code].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  return [
    a.street,
    cityState,
    a.country && a.country !== "US" && a.country !== "USA" ? a.country : ""
  ]
    .filter(Boolean)
    .join(", ");
};

export const defaultEntity = (
  company: Pick<Company, "entities">
): CompanyEntity | undefined =>
  company.entities.find((e) => e.is_default) ?? company.entities[0];

const nameKey = (name: string) =>
  name
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** The existing company with this name or FEIN (case and punctuation aside), as the server refuses a second. */
export const sameCompany = <C extends Pick<Company, "name" | "entities">>(
  companies: C[],
  name: string,
  fein: string
): C | undefined => {
  const key = nameKey(name);
  const digits = fein.replace(/\D/g, "");
  return companies.find(
    (c) =>
      (key &&
        [c.name, ...c.entities.map((e) => e.legal_name)].some(
          (n) => nameKey(n) === key
        )) ||
      (digits.length === 9 &&
        c.entities.some((e) => e.fein.replace(/\D/g, "") === digits))
  );
};

/** "Northwind Labs, Inc. and 1 more entity" */
export const entitySummary = (company: Pick<Company, "entities">): string => {
  const first = defaultEntity(company);
  if (!first) return "No legal entity yet";
  const more = company.entities.length - 1;
  return more > 0
    ? `${first.legal_name} and ${more} more ${
        more === 1 ? "entity" : "entities"
      }`
    : first.legal_name;
};

export const ROLE_LABEL: Record<ContactRole, string> = {
  primary: "Primary",
  approver: "Approver",
  requester: "Requester",
  signatory: "Signatory",
  billing: "Billing"
};

export const ACCESS_LABEL: Record<PortalAccess, string> = {
  all: "All employees",
  requested: "Employees they requested",
  sign: "Signing only",
  spend: "Spend only",
  none: "No portal access"
};

const DAY = 86_400_000;

export const daysUntil = (iso: string, now: Date = new Date()): number =>
  Math.ceil(
    ((calendarDay(iso) ?? new Date(iso)).getTime() - now.getTime()) / DAY
  );

/** Due within 30 days. */
export const isSoon = (
  d: Deadline | null | undefined,
  now: Date = new Date()
): boolean => Boolean(d) && daysUntil((d as Deadline).due_date, now) <= 30;

export const formatDeadline = (d: Deadline | null | undefined): string => {
  if (!d) return "None coming up";
  const date = (
    calendarDay(d.due_date) ?? new Date(d.due_date)
  ).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
  return `${d.title} · ${date}`;
};

export const formatDate = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      })
    : "";

export type CaseFilter = {
  query: string;
  show: "open" | "all";
  entityId: string | null;
};

/** Filters keep a dependent with its principal: a matching principal shows its dependents. */
export const filterCases = (
  rows: CompanyCaseRow[],
  f: CaseFilter
): CompanyCaseRow[] => {
  const q = f.query.trim().toLowerCase();
  const matches = (r: CompanyCaseRow) =>
    (f.show === "all" || r.open) &&
    (!f.entityId || r.entity_id === f.entityId) &&
    (!q ||
      [r.name, r.visa, r.job, r.client_matter_id, r.cost_center]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)));
  const kept = new Set(
    rows
      .filter((r) => !r.dependent_of && matches(r))
      .map((r) => r.individual_id)
  );
  return rows.filter((r) =>
    r.dependent_of
      ? kept.has(r.dependent_of) || matches(r)
      : kept.has(r.individual_id)
  );
};

export type CompanySort = "default" | "name" | "open" | "deadline";

/** Ordering for the Display menu; unknown deadlines sort last. */
export const sortCompanies = (
  rows: CompanyListRow[],
  sort: CompanySort
): CompanyListRow[] => {
  if (sort === "default") return rows;
  const next = [...rows];
  switch (sort) {
    case "name":
      return next.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      );
    case "open":
      return next.sort((a, b) => b.open_cases - a.open_cases);
    case "deadline":
    default:
      return next.sort(
        (a, b) =>
          (a.next_deadline ? Date.parse(a.next_deadline.due_date) : Infinity) -
          (b.next_deadline ? Date.parse(b.next_deadline.due_date) : Infinity)
      );
  }
};
